import type { ChainType } from '@polaris/core/models'
import { mpcKeyHierarchyInitAtom } from '@polaris/core/stores/mpc/queries'
import {
  type MutationKey,
  useIsMutating,
  useMutation,
  useMutationState,
} from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { useGuestModeSessionSigs } from '@/src/hooks/mpc/use-guest-mode-session-sigs'
import { swapHotkeyAtom } from '@/src/stores/swap/atoms'
import { useWalletKeyring } from '../wallet/use-wallet-keyring'

interface UseActivateAccountMutationProps {
  chainType: ChainType
}

const mutationKey: MutationKey = ['activate-account']

export const useActivateAccountError = () => {
  const results = useMutationState({
    filters: {
      mutationKey,
    },
    select(mutation) {
      return mutation.state?.error
    },
  })

  return {
    isError: results.length > 0 && !!results.at(-1),
    error: results.length > 0 ? results.at(-1) : undefined,
  }
}

/**
 * Returns true if account is being activated OR if key hierarchy is being initialized.
 * This ensures the "Activating account" UI shows during both processes.
 */
export const useActivateAccountIsMutating = () => {
  const count = useIsMutating({
    mutationKey,
  })
  const keyHierarchyInit = useAtomValue(mpcKeyHierarchyInitAtom)

  // Show activation UI if either:
  // 1. The activation mutation is running
  // 2. Key hierarchy is being initialized (isFetching but not yet successful)
  return (
    count > 0 || (keyHierarchyInit.isFetching && !keyHierarchyInit.isSuccess)
  )
}

export const useActivateAccount = () => {
  const swapHotkey = useAtomValue(swapHotkeyAtom)
  const { guestModeSessionKeyData } = useGuestModeSessionSigs()
  // MPC is now always enabled - no longer controlled by feature flag
  const { generateSessionSigs } = useWalletKeyring()

  return useMutation({
    mutationKey,
    scope: {
      id: 'unique-active-account',
    },
    mutationFn: async ({ chainType }: UseActivateAccountMutationProps) => {
      if (!swapHotkey || !guestModeSessionKeyData) {
        // MPC is now always enabled
        await generateSessionSigs(chainType)
      } else {
        console.warn(
          'Account already activated - skipping generation of hotkey or mpc since it already exists',
        )
      }
    },
    onError(error) {
      console.error('Account activation error:', error)
    },
  })
}
